import React, { useRef, useState } from 'react';
import { Popper } from '@amzn/storm-ui-v3';
import StormDatePicker, { DatePickerInput as StormDatePickerInput } from '@amzn/storm-ui-date-picker-v3';
import { useLocale } from '@urc-ui/hooks/date-fns-locale';
import { useLocalizedDate } from '@urc-ui/hooks/i18n';
import { getTimezone } from '@urc-ui/services/app-context';
import { useDatePickerDates } from './use-date-picker-dates';
import { DatePickerProps } from './types';

/**
 * This component renders a popper with Storm's DateInput as the trigger. Clicking anywhere on the DateInput will open
 * Storm's DatePicker directly below the input. The DatePicker only allows picking future dates starting from tomorrow.
 * @param props see detailed inline documentation on the DatePickerProps types file
 */
const DatePicker = (props: DatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const togglePopover = () => setIsOpen(prevState => !prevState);

  const timeZone = getTimezone();
  const { tomorrow, initialDate } = useDatePickerDates({
    todayUtc: new Date(),
    initialDate: props.initialDate,
    timeZone
  });
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  const datePickerInputValue = useLocalizedDate([{ value: selectedDate }])[0];
  const locale = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  const onDateSelected = (day: Date) => {
    setSelectedDate(day);
    props.onDateSelected(day);
    togglePopover();
  };

  return (
    <Popper
      isOpen={isOpen}
      position='bottom'
      align='start'
      withArrow={false}
      trigger={
        <StormDatePickerInput
          id={'date-picker-input'}
          data-testid={'date-picker-input'}
          hideLabel={true}
          value={datePickerInputValue}
          readOnly={true}
          locale={locale}
          inputRef={inputRef}
          onClick={togglePopover}
        />
      }
    >
      <StormDatePicker
        id={'date-picker'}
        data-testid={'date-picker'}
        locale={locale}
        minDate={tomorrow}
        maxDate={props.maxDate}
        numberOfMonths={1}
        zone={timeZone}
        onChange={onDateSelected}
        date={selectedDate}
        allowNoDate={false}
      />
    </Popper>
  );
};

export { DatePicker };
export type { DatePickerProps };
